import { AssignmentParams } from 'app/models/assignment-params'
import {
  submitForApproval,
  rejectSubmittedAssignment,
  approveSubmittedAssignment,
  deleteDraftAssignment,
} from 'app/state/ducks/assignments/thunks'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { Dispatch } from 'redux'

/**
 * Runs one state change and reports whether it succeeded, with its toast.
 *
 * Defensive on purpose: the API client's error interceptor RESOLVES with
 * `undefined` on an HTTP error, and these thunks' own `.catch` then reads
 * `e.response.data.error` off the TypeError that causes, which throws again.
 * Without this guard a failed write threw out of the click handler: no
 * toast, no re-read, and the row silently kept its old status.
 */
async function runStateChange(
  // a dispatched thunk: Dispatch<any> types it as the thunk, not its promise
  write: () => any,
  succeeded: { title: string; message: string },
  failed: { title: string; message: string },
): Promise<boolean> {
  let response: any
  try {
    response = await write()
  } catch {
    response = undefined
  }
  if (response?.success === true) {
    SuccessNotification(succeeded)
    return true
  }
  ErrorNotification({ title: failed.title, message: response?.payload ?? failed.message })
  return false
}

export function submitAssignment(assignment: AssignmentParams, dispatch: Dispatch<any>) {
  return runStateChange(
    () => dispatch(submitForApproval(assignment)),
    { title: 'Successful Submission', message: 'Assignment has been Submitted for Approval' },
    { title: 'Submission Failed', message: 'Assignment could not be Submitted' },
  )
}

export function rejectAssignment(assignment: AssignmentParams, dispatch: Dispatch<any>) {
  return runStateChange(
    () => dispatch(rejectSubmittedAssignment(assignment)),
    { title: 'Successful Rejection', message: 'Assignment has been moved back to a Draft' },
    { title: 'Rejection Failed', message: 'Assignment could not be Rejected' },
  )
}

export function approveAssignment(assignment: AssignmentParams, dispatch: Dispatch<any>) {
  return runStateChange(
    () => dispatch(approveSubmittedAssignment(assignment)),
    { title: 'Successful Approval', message: 'Assignment has been Approved' },
    { title: 'Approval Failed', message: 'Assignment could not be Approved' },
  )
}

export async function deleteAssignment(
  assignment: AssignmentParams,
  dispatch: Dispatch<any>
) {
  // The thunk's own catch swallows a rejected request and returns nothing, so
  // read the answer defensively: a hard failure must say so and leave the row
  // where it is, not throw out of the click handler.
  let response: any
  try {
    response = await dispatch(deleteDraftAssignment([assignment.id]))
  } catch {
    response = undefined
  }
  // The delete answers per id ({ id, isSuccess, error }), with HTTP 200 even
  // when the ledger refused it; the grid removes only an accepted row, so the
  // toast says deleted only when THIS id was accepted — not on an empty or
  // unrelated answer.
  const results: any[] = Array.isArray(response?.payload) ? response.payload : []
  const mine = results.find((result: any) => result?.id === assignment.id)
  const accepted = !!mine && mine.isSuccess !== false && !mine.error
  if (!accepted) {
    const reason = mine?.error ?? response?.payload?.error
    ErrorNotification({
      title: 'Delete Failed',
      message:
        (typeof reason === 'string' ? reason : reason?.message) ?? 'Assignment could not be deleted',
    })
    return false
  }
  SuccessNotification({
    title: 'Successful Deletion',
    message: 'Assignment has been Deleted',
  })
  return true
}
